import { useLocation } from '@tanstack/react-router'
import { DownloadIcon, PanelLeft, XIcon } from 'lucide-react'
import { type PropsWithChildren, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { ImperativePanelHandle } from 'react-resizable-panels'

import { AppSidebar } from '@/components/app-sidebar'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import {
  Sidebar,
  SidebarContent,
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from '@/components/ui/sidebar'
import { useNavigationConfig } from '@/config/useNavigationConfig'
import { useDocumentPreview } from '@/contexts/document-preview-context'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/redux/hooks'

import PDFViewer from '../pdf-viewer'
import { SidebarAssistantToggle } from '../sidebar-assistant-toggle'
import { Button } from '../ui/button'
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs'
import AiChat from './ai-chat'

const RightSidebarContent = () => {
  const { previewMimeType, previewUrl, selectedIndex, setPreviewUrl, setSelectedIndex, sources } =
    useDocumentPreview()
  const { t } = useTranslation()

  const renderContent = useCallback(() => {
    if (!previewUrl) {
      return (
        <div className="p-4 text-muted-foreground">{t('documentPreview.noDocumentSelected')}</div>
      )
    }

    if (previewMimeType === 'application/pdf') {
      return <PDFViewer url={previewUrl} />
    }

    if (previewMimeType?.startsWith('image/')) {
      return (
        <div className="flex h-full items-center justify-center">
          <img
            alt={t('documentPreview.imageAlt')}
            className="max-h-full max-w-full object-contain"
            src={previewUrl}
          />
        </div>
      )
    }

    return <div className="p-4 text-muted-foreground">{t('documentPreview.unsupportedType')}</div>
  }, [previewUrl, previewMimeType, t])

  return (
    <div className="bg-layer-1 h-full ">
      <div className="p-2 flex items-center gap-2">
        <div className="flex-1 min-w-0 overflow-x-auto">
          {sources.length > 1 && (
            <Tabs onValueChange={v => setSelectedIndex(Number(v))} value={String(selectedIndex)}>
              <TabsList variant="segment">
                {sources.map((src, i) => (
                  <TabsTrigger
                    className="max-w-[160px] truncate text-sm"
                    key={i}
                    title={src.label}
                    value={String(i)}
                    variant="segment"
                  >
                    {src.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          )}
        </div>
        {previewUrl && (
          <Button asChild className="shrink-0" variant="outline">
            <a download href={previewUrl} rel="noreferrer" target="_blank">
              <DownloadIcon />
              {t('documentPreview.downloadButton')}
            </a>
          </Button>
        )}
        <Button className="shrink-0" onClick={() => setPreviewUrl(null)} variant="outline">
          <XIcon />
          {t('documentPreview.closeButton')}
        </Button>
      </div>
      {renderContent()}
    </div>
  )
}

const MainContent = ({
  activeItemTitle,
  children,
  parentItem,
}: {
  activeItemTitle?: string
  children: React.ReactNode
  parentItem?: { title: string; url: string }
}) => {
  const pathname = useLocation().pathname
  const { previewUrl } = useDocumentPreview()
  const { toggleSidebar } = useSidebar()
  const { t } = useTranslation()
  const isSideChatOpen = useAppSelector(state => state.assistant.isSideChatOpen)
  const isChatFullscreen = useAppSelector(state => state.assistant.isChatFullscreen)
  const selectedIndividualId = useAppSelector(state => state.settings.selectedIndividualId)
  const isMobile = useIsMobile()

  const chatTakesFullScreen = isChatFullscreen || (isMobile && isSideChatOpen)
  const previewTakesFullScreen = isMobile && !!previewUrl
  return (
    <SidebarProvider className="flex-1 min-h-0 h-full overflow-hidden" defaultOpen={false}>
      <SidebarInset className="min-h-0 overflow-hidden">
        <header className="sticky top-0 z-20 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
          <Breadcrumb>
            <BreadcrumbList>
              <Button
                className="group-data-[collapsible=icon]:size-10 lg:hidden"
                onClick={toggleSidebar}
                size="icon"
                variant="ghost"
              >
                <PanelLeft />
                <span className="sr-only">{t('global.toggleSidebar')}</span>
              </Button>
              {parentItem && activeItemTitle && !isMobile ? (
                <>
                  <BreadcrumbItem>
                    <BreadcrumbLink href={parentItem.url}>{parentItem.title}</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{activeItemTitle}</BreadcrumbPage>
                  </BreadcrumbItem>
                </>
              ) : activeItemTitle ? (
                <BreadcrumbItem>
                  <BreadcrumbPage>{activeItemTitle}</BreadcrumbPage>
                </BreadcrumbItem>
              ) : null}
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto flex items-center gap-2">
            <SidebarAssistantToggle />
          </div>
        </header>
        <div className="flex flex-1 flex-col overflow-hidden">
          <ResizablePanelGroup direction="horizontal">
            <ResizablePanel
              className={cn({ hidden: chatTakesFullScreen || previewTakesFullScreen })}
            >
              <div className="h-full overflow-y-auto">
                <div
                  className={cn(
                    'flex flex-1 min-h-full flex-col gap-4 p-4 md:p-10 md:w-[min(100%,_1400px)] md:mx-auto relative',
                    {
                      'p-0 md:p-0': pathname.includes('ai-chat'),
                    },
                  )}
                >
                  {children}
                </div>
              </div>
            </ResizablePanel>

            {!isMobile && (
              <ResizableHandle
                className="w-4 data-[visible=false]:hidden"
                data-visible={!!previewUrl}
                withHandle
              />
            )}
            <ResizablePanel
              className="data-[visible=false]:hidden"
              collapsedSize={0}
              collapsible={false}
              data-visible={!!previewUrl}
              defaultSize={isMobile ? 100 : 40}
              maxSize={isMobile ? 100 : 60}
              minSize={30}
            >
              <div className="h-full border-l">{previewUrl && <RightSidebarContent />}</div>
            </ResizablePanel>

            <ResizableHandle
              className="w-4 data-[visible=false]:hidden"
              data-visible={isSideChatOpen && !chatTakesFullScreen}
              withHandle
            />
            <ResizablePanel
              className="data-[visible=false]:hidden"
              data-visible={isSideChatOpen}
              defaultSize={isMobile ? 100 : 40}
              maxSize={isMobile ? 100 : 60}
              minSize={30}
            >
              <div className={cn('h-full', { 'pl-4': chatTakesFullScreen })}>
                {selectedIndividualId && <AiChat layout="sidebar" />}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      </SidebarInset>
      <Sidebar
        className="hidden"
        collapsible="none"
        side="right"
        style={{ border: 'none', width: '0px' }}
      >
        <SidebarContent />
      </Sidebar>
    </SidebarProvider>
  )
}

const SUB_ROUTES: Record<string, { parentUrl: string; title: string }> = {
  '/documents/combine': { parentUrl: '/documents', title: 'uploadPage.header.title' },
  '/documents/upload': { parentUrl: '/documents', title: 'uploadPage.header.title' },
  '/notes/new': { parentUrl: '/notes', title: 'notesPage.create.title' },
}

// Resolve a sub-route breadcrumb, including dynamic detail paths that can't be
// keyed statically (e.g. /notes/<noteId>).
const resolveSubRoute = (pathname: string): { parentUrl: string; title: string } | undefined => {
  if (SUB_ROUTES[pathname]) return SUB_ROUTES[pathname]

  // Note detail page: /notes/<id> (excluding the index and /notes/new).
  if (/^\/notes\/[^/]+$/.test(pathname) && pathname !== '/notes/new') {
    return { parentUrl: '/notes', title: 'notesPage.detail.noteSection' }
  }

  // Uploading into an errand: /errands/<id>/upload. Belongs under Ärenden, not Dokument — it is
  // reached from an errand and returns to it.
  if (/^\/errands\/[^/]+\/upload$/.test(pathname)) {
    return { parentUrl: '/errands', title: 'uploadPage.header.title' }
  }

  return undefined
}

const AppNavigationShell = ({ children }: PropsWithChildren) => {
  const pathname = useLocation().pathname
  const navigationConfig = useNavigationConfig()
  const { t } = useTranslation()

  const subRoute = resolveSubRoute(pathname)

  const activeGroup = navigationConfig.navMain.find(group =>
    group.items.some(item => item.url === (subRoute?.parentUrl ?? pathname)),
  )
  const parentNavItem = activeGroup?.items.find(
    item => item.url === (subRoute?.parentUrl ?? pathname),
  )

  const activeItemTitle: string | undefined = subRoute
    ? (t as any)(subRoute.title)
    : parentNavItem?.title
  const parentItem =
    subRoute && parentNavItem ? { title: parentNavItem.title, url: parentNavItem.url } : undefined

  return (
    <SidebarProvider className="min-h-0 h-svh w-full overflow-hidden">
      <AppSidebar />
      <SidebarInset className="min-h-0 overflow-hidden ">
        <MainContent activeItemTitle={activeItemTitle} parentItem={parentItem}>
          {children}
        </MainContent>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default AppNavigationShell
